import 'react-international-phone/style.css';
import { ReactNode, useState, useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import {
  useGetSignature,
  useUpdateMe,
  useUploadProfilePhoto,
  useUploadSignature,
} from '@/lib/api/me.ts';
import { UpdateMeResponse } from '@inversor-hub/types';
import { GoogleMapsProvider } from '@/context/GoogleMapsProvider.tsx';
import PsiConfigSectionForm from '../../psi/PsiConfigSectionForm.tsx';
import PsiContractingSection from '@/features/psi/PsiContractingSection';
import InvestorConfigSection from '../../investor/InvestorConfigSection.tsx';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UserDetailsSectionForm from './UserDetailsSectionForm.tsx';
import { useSearchParams } from 'react-router-dom';
import BillingSection from '@/features/billing/components/BillingSection.tsx';
import { TEMPORARY_FREE_BILLING_BETA } from '@/features/billing/temporary-free-billing-beta.ts';
import MonthlyInvestmentEmailPreferences from './MonthlyInvestmentEmailPreferences.tsx';

interface Props {
  currentUser: UpdateMeResponse;
}

type ProfileAccordionSection =
  | 'contratacion'
  | 'personal'
  | 'configuracion'
  | 'notificaciones'
  | 'facturacion';

function ProfileSection({
  id,
  title,
  description,
  icon,
  expanded,
  onChange,
  children,
}: {
  id: ProfileAccordionSection;
  title: string;
  description: string;
  icon: ReactNode;
  expanded: boolean;
  onChange: (expanded: boolean) => void;
  children: ReactNode;
}) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_event, nextExpanded) => onChange(nextExpanded)}
      sx={{
        border: '1px solid',
        borderColor: expanded ? 'primary.main' : 'divider',
        borderRadius: '16px !important',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        id={`${id}-header`}
        aria-controls={`${id}-content`}
        expandIcon={<ExpandMoreIcon sx={{ color: expanded ? 'primary.main' : 'text.secondary' }} />}
        sx={{
          px: { xs: 2, sm: 3 },
          minHeight: 88,
          borderRadius: '16px',
          '& .MuiAccordionSummary-content': { my: 2, gap: 2, alignItems: 'center' },
          '&:hover': { bgcolor: 'action.hover' },
          '&.Mui-focusVisible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 3,
          },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: expanded ? 'primary.50' : 'grey.50',
            color: expanded ? 'primary.dark' : 'text.secondary',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Typography component="h2" variant="subtitle1" fontWeight={600} lineHeight={1.4}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
            {description}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3 }}>{children}</Box>
      </AccordionDetails>
    </Accordion>
  );
}

const UpdateProfile = ({ currentUser }: Props) => {
  const { mutateAsync: uploadSignature } = useUploadSignature();
  const { data: signatureDataUrl } = useGetSignature();
  const { mutateAsync: updateMe } = useUpdateMe();
  const { mutateAsync: uploadProfilePhoto } = useUploadProfilePhoto();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const isPsi = currentUser.role === 'psi';
  const showBillingSection = currentUser.role === 'investor' && !TEMPORARY_FREE_BILLING_BETA;
  const activeTab =
    requestedTab === 'personal' ||
    requestedTab === 'configuracion' ||
    (requestedTab === 'contratacion' && isPsi) ||
    (requestedTab === 'notificaciones' && !isPsi) ||
    (requestedTab === 'facturacion' && showBillingSection)
      ? requestedTab
      : false;
  const [expandedSection, setExpandedSection] = useState<ProfileAccordionSection | false>(
    activeTab
  );

  useEffect(() => {
    setExpandedSection(activeTab);
  }, [activeTab]);

  const sectionProps = (id: ProfileAccordionSection) => ({
    id,
    expanded: expandedSection === id,
    onChange: (expanded: boolean) => setExpandedSection(expanded ? id : false),
  });

  return (
    <GoogleMapsProvider>
      <Stack spacing={2} sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
        <ProfileSection
          {...sectionProps('personal')}
          title="Datos personales"
          description="Tu información de contacto, documento de identidad y firma."
          icon={<PersonOutlineIcon />}
        >
          <UserDetailsSectionForm
            currentUser={currentUser}
            signatureDataUrl={signatureDataUrl}
            uploadProfilePhoto={uploadProfilePhoto}
            uploadSignature={uploadSignature}
            updateMe={updateMe}
          />
        </ProfileSection>
        <ProfileSection
          {...sectionProps('configuracion')}
          title={isPsi ? 'Perfil profesional' : 'Preferencias de inversión'}
          description={
            isPsi
              ? 'Especialidades, honorarios y datos bancarios.'
              : 'Tus objetivos, presupuesto y zonas de interés.'
          }
          icon={isPsi ? <WorkOutlineIcon /> : <AccountBalanceOutlinedIcon />}
        >
          {isPsi ? <PsiConfigSectionForm /> : <InvestorConfigSection />}
        </ProfileSection>
        {isPsi ? (
          <ProfileSection
            {...sectionProps('contratacion')}
            title="Datos de contratación"
            description="Elige si firmas tus colaboraciones a título personal o como empresa."
            icon={<DrawOutlinedIcon />}
          >
            <PsiContractingSection />
          </ProfileSection>
        ) : (
          <ProfileSection
            {...sectionProps('notificaciones')}
            title="Notificaciones"
            description="Elige los correos que quieres recibir."
            icon={<MailOutlineIcon />}
          >
            <MonthlyInvestmentEmailPreferences />
          </ProfileSection>
        )}
        {showBillingSection && (
          <ProfileSection
            {...sectionProps('facturacion')}
            title="Facturación"
            description="Tu plan, suscripción y datos de facturación."
            icon={<ReceiptLongOutlinedIcon />}
          >
            <BillingSection currentUser={currentUser} />
          </ProfileSection>
        )}
      </Stack>
    </GoogleMapsProvider>
  );
};

export default UpdateProfile;
